export interface PortfolioItem {
  slug: string;
  title: string;
  client: string;
  industry: string;
  tech: string[];
  scale: "Startup" | "SME" | "Enterprise";
  summary: string;
  challenge: string;
  research: string;
  design: string;
  development: string;
  results: {
    metrics: string[];
    description: string;
  };
  feedback: {
    quote: string;
    author: string;
    role: string;
  };
}

export const portfolioData: PortfolioItem[] = [
  {
    slug: "apex-logistics-crm",
    title: "Apex Logistics Custom CRM Integration",
    client: "Apex Logistics",
    industry: "Logistics",
    scale: "Enterprise",
    tech: ["Node.js", "React", "PostgreSQL", "WhatsApp Cloud API", "Docker"],
    summary: "Automated container routing and driver WhatsApp notification workflows, replacing manual dispatch systems.",
    challenge: "Apex Logistics was losing over 40 dispatcher hours every week to manual phone updates. Drivers had to coordinate route changes over phone calls, leading to a 12% order routing failure rate and delayed deliveries.",
    research: "We audited their dispatcher console workflow and discovered that 70% of phone updates involved simple status checks. We proposed replacing phone coordinates with automated location triggers linked to the WhatsApp Business API.",
    design: "We designed a unified, low-latency React dashboard for dispatchers. The UI prioritizes a visual map grid, list filtering for late drivers, and logs displaying outgoing WhatsApp message read-states.",
    development: "We built the backend API using Node.js and PostgreSQL. We structured a custom polling daemon that queries driver locations, triggers WhatsApp alerts when containers move, and tracks responses in a transaction ledger.",
    results: {
      metrics: [
        "+240% Order Fulfillment Rate",
        "0 dispatcher phone calls for routing",
        "-45 hours of weekly dispatcher labor",
      ],
      description: "Apex Logistics scaled their daily container routes from 80 to 270 without adding dispatcher headcount, achieving zero communication bottlenecks.",
    },
    feedback: {
      quote: "Our dispatchers no longer spend their days on manual phone updates. The automated WhatsApp routing is flawless, and the React dashboard is incredibly fast.",
      author: "Karan Johar",
      role: "Operations Chief, Apex Logistics",
    },
  },
  {
    slug: "dentin-luxury-portal",
    title: "Dentin Luxury Healthcare Portal",
    client: "Dentin Dental Clinics",
    industry: "Healthcare",
    scale: "SME",
    tech: ["Next.js", "TypeScript", "Tailwind CSS", "Firebase Auth", "Firestore"],
    summary: "High-performance dental booking suite with custom calendar sync and automated SMS reminders.",
    challenge: "Dentin's legacy Wordpress site loaded in 4.5 seconds, leading to a high 38% user drop-off rate on their appointment page. Patient calendars did not sync in real-time, resulting in double-booking conflicts.",
    research: "We analyzed their Lighthouse metrics and patient drop-off flows. We discovered that script load delays and static forms caused patient drop-offs. We recommended a high-speed Next.js app with real-time Firestore calendar listeners.",
    design: "We structured a premium, minimalist, luxury clinic interface. Large headings (Poppins), soft slate gradients, and a single-screen calendar widget focus the patient's attention strictly on selecting an open booking slot.",
    development: "We built the site on Next.js 14 App Router for Server-side rendering (RSC) speed. We integrated Firebase Auth for patient login profiles, Firestore for real-time schedule locking, and Twilio API for appointment SMS reminders.",
    results: {
      metrics: [
        "+45% Patient Booking Conversion",
        "1.1s Largest Contentful Paint (LCP)",
        "Zero appointment scheduling conflicts",
      ],
      description: "Dentin increased monthly new patient acquisitions by 45%, establishing a premium brand presence matching their luxury offline clinics.",
    },
    feedback: {
      quote: "We now secure high-value implant consultations automatically. Patients frequently comment on how fast and elegant the booking calendar behaves.",
      author: "Dr. Sandeep Kumar",
      role: "Founder, Dentin Clinics",
    },
  },
  {
    slug: "horizon-real-estate-erp",
    title: "Horizon Properties Asset ERP",
    client: "Horizon Real Estate",
    industry: "Real Estate",
    scale: "Enterprise",
    tech: ["Python", "FastAPI", "MongoDB", "AWS S3", "Cloudflare"],
    summary: "Enterprise asset management portal with document workflows, secure lease agreements, and user access levels.",
    challenge: "Horizon managed 1,400 commercial leases across fragmented spreadsheets. Lease renewal agreements were frequently missed, and calculating payment metrics across agents took days of manual collation.",
    research: "We audited their spreadsheet structures and lease agreements. We recommended a unified document database index (MongoDB) with automated cron workers to flag upcoming renewals and compile cash-flow metrics.",
    design: "We built a clean, administrative dashboard layout inspired by Linear. It uses high contrast visible grids, a side navigation panel, dynamic search filters, and subtle status badges for leases.",
    development: "We compiled the backend API using FastAPI for fast JSON serialization. We set up an AWS S3 file vault to store lease documents securely, encrypted at rest, and used Cloudflare edge caching to speed up PDF document loads.",
    results: {
      metrics: [
        "-35% Administrative Hours",
        "99.99% peak server load uptime",
        "Zero missed lease renewal flags",
      ],
      description: "Horizon automated their lease alert pipeline and digitized their document vault, saving their administrative staff over 120 manual hours every month.",
    },
    feedback: {
      quote: "Our data security audits pass instantly now that all leases reside in an encrypted cloud index. The alert triggers have completely removed missed lease renewals.",
      author: "Meera Sen",
      role: "CTO, Horizon Properties",
    },
  },
];
